import React, { useState, useRef, useEffect, lazy, Suspense, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, ChevronRight, ChevronLeft, Download, Globe, Lock, Eye, EyeOff, Shield, Home, LogOut, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import TajweedQuiz from '@/components/TajweedQuiz';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// --- Lazy-loaded lessons ---
const lessonLoaders = {
  1: lazy(() => import('./lessons/Lesson1')),
  2: lazy(() => import('./lessons/Lesson2')),
  3: lazy(() => import('./lessons/Lesson3')),
  4: lazy(() => import('./lessons/Lesson4')),
  5: lazy(() => import('./lessons/Lesson5')),
  6: lazy(() => import('./lessons/Lesson6')),
  7: lazy(() => import('./lessons/Lesson7')),
  8: lazy(() => import('./lessons/Lesson8')),
  9: lazy(() => import('./lessons/Lesson9')),
  10: lazy(() => import('./lessons/Lesson13')),
  11: lazy(() => import('./lessons/Lesson10')),
  12: lazy(() => import('./lessons/Lesson11')),
  13: lazy(() => import('./lessons/Lesson12')),
  14: lazy(() => import('./lessons/Lesson14')),
  15: lazy(() => import('./lessons/Lesson15')),
  16: lazy(() => import('./lessons/Lesson16')),
  17: lazy(() => import('./lessons/Lesson17')),
  18: lazy(() => import('./lessons/Lesson18')),
  19: lazy(() => import('./lessons/Lesson19')),
  20: lazy(() => import('./lessons/Lesson20')),
  21: lazy(() => import('./lessons/Lesson21')),
  22: lazy(() => import('./lessons/Lesson22')),
  23: lazy(() => import('./lessons/Lesson23')),
  24: lazy(() => import('./lessons/Lesson24')),
  25: lazy(() => import('./lessons/Lesson25')),
  26: lazy(() => import('./lessons/Lesson26')),
};

// --- Chapter metadata (NO content, just titles) ---
const chapters = [
  { id: 1, title: 'Introduktion till Tajweed', title_en: 'Introduction to Tajweed', arabicTitle: 'مقدمة في التجويد' },
  { id: 2, title: "Isti\u2019\u0101dhah & Basmala", title_en: "Seeking Refuge & Basmala", arabicTitle: 'الاستعاذة والبسملة' },
  { id: 3, title: 'De arabiska bokst\u00e4verna & vokaler', title_en: 'The Arabic Letters & Vowels', arabicTitle: 'الحروف العربية والحركات' },
  { id: 4, title: 'Suk\u016bn & Shaddah', title_en: 'Suk\u016bn & Shaddah', arabicTitle: 'السكون والشدة' },
  { id: 5, title: 'Madd \u2014 grundl\u00e4ggande', title_en: 'Madd \u2014 Basics', arabicTitle: 'المد الأساسي' },
  { id: 6, title: 'Artikulationspunkter', title_en: 'Articulation Points', arabicTitle: 'مخارج الحروف' },
  { id: 7, title: 'Bokst\u00e4vernas egenskaper', title_en: 'Letter Attributes', arabicTitle: 'صفات الحروف' },
  { id: 8, title: "Tafkh\u012Bm & Tarq\u012Bq \u2014 Tunga & mjuka bokst\u00e4ver", title_en: 'Tafkh\u012Bm & Tarq\u012Bq', arabicTitle: 'التفخيم والترقيق' },
  { id: 9, title: 'Vanliga uttalssfel', title_en: 'Common Pronunciation Mistakes', arabicTitle: 'أبرز الأخطاء عند نطق حروف الهجاء' },
  { id: 10, title: 'Idgh\u0101m \u2014 Bokstavsrelationer', title_en: 'Idgh\u0101m \u2014 Letter Merging', arabicTitle: 'الحرفان الملتقيان — الإدغام' },
  { id: 11, title: 'N\u016bn S\u0101kinah & Tanw\u012Bn', title_en: 'N\u016bn S\u0101kinah & Tanw\u012Bn Rules', arabicTitle: 'أحكام النون الساكنة والتنوين' },
  { id: 12, title: 'M\u012Bm S\u0101kinah-regler', title_en: 'M\u012Bm S\u0101kinah Rules', arabicTitle: 'أحكام الميم الساكنة' },
  { id: 13, title: 'Ghunnah \u2014 Nasalering', title_en: 'Ghunnah \u2014 Nasalization', arabicTitle: 'الغنة' },
  { id: 14, title: 'L\u0101m-regler & Solbokst\u00e4ver', title_en: 'L\u0101m Rules & Sun Letters', arabicTitle: 'أحكام اللام' },
  { id: 15, title: 'Madd \u2014 F\u00f6rl\u00e4ngningsregler', title_en: 'Madd \u2014 Elongation Rules', arabicTitle: 'أحكام المد' },
  { id: 16, title: 'Madd-prioritet & tecken i Koranen', title_en: 'Madd Priority & Quranic Signs', arabicTitle: 'ترتيب المدود وعلاماتها' },
  { id: 17, title: 'Qalqala \u2014 Studsande ljud', title_en: 'Qalqala \u2014 Bouncing Sound', arabicTitle: 'القلقلة' },
  { id: 18, title: 'Regler f\u00f6r stopp & start', title_en: 'Stopping & Starting Rules', arabicTitle: 'الوقف والابتداء' },
  { id: 19, title: 'Alif-regler & De sju aliferna', title_en: 'Alif Rules & The Seven Alifs', arabicTitle: 'الألفات السبعة' },
  { id: 20, title: 'Hamzatul Wa\u1e63l', title_en: 'Hamzat al-Wa\u1e63l', arabicTitle: 'همزة الوصل' },
  { id: 21, title: "Hamzat al-Qa\u1e6d\u2019", title_en: "Hamzat al-Qa\u1e6d\u2019", arabicTitle: 'همزة القطع' },
  { id: 22, title: 'M\u00f6te av s\u0101kin-bokst\u00e4ver', title_en: 'Meeting of Two S\u0101kin Letters', arabicTitle: 'التقاء الساكنين' },
  { id: 23, title: 'Ar-Rawm & Al-Ishmam', title_en: 'Ar-Rawm & Al-Ishm\u0101m', arabicTitle: 'الروم والإشمام' },
  { id: 24, title: 'Koranord med speciella regler', title_en: 'Quranic Words with Special Rulings', arabicTitle: 'كلمات قرآنية لها وضع خاص' },
  { id: 25, title: 'Betoning i Koranrecitation', title_en: 'Emphasis in Quran Recitation', arabicTitle: 'النبر في تلاوة القرآن' },
  { id: 26, title: 'Att memorera Koranen', title_en: 'Memorizing the Quran', arabicTitle: 'حفظ القرآن الكريم' },
];

const BOOK_LEVELS = [
  { id: 1, name_sv: 'Nivå 1 — Grunder', name_en: 'Level 1 — Fundamentals', arabicTitle: 'الأساسيات', color: '#22c55e', icon: '١', chapterIds: [1,2,3,4,5] },
  { id: 2, name_sv: 'Nivå 2 — Uttal', name_en: 'Level 2 — Pronunciation', arabicTitle: 'النطق والمخارج', color: '#3b82f6', icon: '٢', chapterIds: [6,7,8,9,10] },
  { id: 3, name_sv: 'Nivå 3 — Regler', name_en: 'Level 3 — Rules', arabicTitle: 'أحكام التجويد', color: '#f97316', icon: '٣', chapterIds: [11,12,13,14,15,16,17] },
  { id: 4, name_sv: 'Nivå 4 — Avancerat', name_en: 'Level 4 — Advanced', arabicTitle: 'المستوى المتقدم', color: '#a855f7', icon: '٤', chapterIds: [18,19,20,21,22,23,24,25,26] },
];

const getChapterLevel = (chId) => BOOK_LEVELS.find(l => l.chapterIds.includes(chId)) || BOOK_LEVELS[0];

// --- Loading spinner ---
const LessonLoader = () => (
  <div className="flex items-center justify-center py-20">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--gold)', borderTopColor: 'transparent' }}/>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Laddar lektion...</p>
    </div>
  </div>
);

// ==========================================
// LOGIN COMPONENT
// ==========================================
const STUDENT_KEY = 'quran2026';

const BookLogin = ({ onLogin }) => {
  const [showTeacher, setShowTeacher] = useState(false);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const handleTeacherLogin = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    if (pw === 'ustadhisma2026') {
      sessionStorage.setItem('tajweed-auth', 'true');
      sessionStorage.setItem('tajweed-role', 'admin');
      sessionStorage.setItem('tajweed-name', 'Ustadh Ismail');
      onLogin({ role: 'admin', name: 'Ustadh Ismail' });
    } else { setErr('Fel lösenord'); }
    setLoading(false);
  };

  const handleStudentCode = (e) => {
    e.preventDefault(); setErr('');
    if (code.trim() === STUDENT_KEY) {
      sessionStorage.setItem('tajweed-auth', 'true');
      sessionStorage.setItem('tajweed-role', 'student');
      sessionStorage.setItem('tajweed-name', 'Elev');
      onLogin({ role: 'student', name: 'Elev' });
    } else { setErr('Fel kod — kontakta din lärare'); }
  };

  if (showTeacher) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="max-w-sm w-full p-8 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
          <div className="text-center mb-6">
            <Shield size={32} className="mx-auto text-gold mb-3"/>
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Lärarinloggning</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Ange lösenord för lärarläge</p>
          </div>
          <form onSubmit={handleTeacherLogin}>
            <div className="relative">
              <input type={show ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} placeholder="Lösenord"
                className="w-full p-3 pr-10 rounded-lg text-sm" data-testid="tajweed-password"
                style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}/>
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3" style={{ color: 'var(--text-muted)' }}>
                {show ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>
            </div>
            {err && <p className="text-red-500 text-sm mt-2">{err}</p>}
            <button type="submit" disabled={loading} className="btn-gold w-full mt-4" data-testid="tajweed-login-btn">
              {loading ? 'Verifierar...' : 'Logga in'}
            </button>
          </form>
          <button onClick={() => { setShowTeacher(false); setErr(''); setPw(''); }} className="w-full mt-3 text-sm text-center py-2" style={{ color: 'var(--text-muted)' }} data-testid="tajweed-back-to-student">
            Tillbaka
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-sm w-full p-8 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
        <div className="text-center mb-6">
          <Lock size={32} className="mx-auto text-gold mb-3"/>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Tajweed Guide</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Ange koden du fått av din lärare</p>
        </div>
        <form onSubmit={handleStudentCode}>
          <div className="relative">
            <input type={show ? 'text' : 'password'} value={code} onChange={e => setCode(e.target.value)} placeholder="Ange elevkod"
              className="w-full p-3 pr-10 rounded-lg text-sm" data-testid="tajweed-access-code"
              style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}/>
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3" style={{ color: 'var(--text-muted)' }}>
              {show ? <EyeOff size={18}/> : <Eye size={18}/>}
            </button>
          </div>
          {err && <p className="text-red-500 text-sm mt-2">{err}</p>}
          <button type="submit" className="btn-gold w-full mt-4" data-testid="tajweed-open-btn">
            Öppna boken
          </button>
        </form>
        <button onClick={() => setShowTeacher(true)} className="w-full mt-3 text-sm text-center py-2" style={{ color: 'var(--text-muted)' }} data-testid="tajweed-teacher-link">
          Lärare? Logga in här
        </button>
      </div>
    </div>
  );
};

// ==========================================
// TABLE OF CONTENTS
// ==========================================
const BookTOC = React.memo(({ sv, selectedLevel, setSelectedLevel, onSelectChapter }) => (
  <div className="space-y-4" data-testid="tajweed-toc">
    <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
      <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
        <BookOpen className="inline mr-2 text-gold" size={24}/>{sv ? 'Innehållsförteckning' : 'Table of Contents'}
      </h2>
      <div className="relative" data-testid="level-dropdown">
        <select value={selectedLevel} onChange={(e) => setSelectedLevel(Number(e.target.value))}
          className="appearance-none pl-4 pr-10 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all"
          style={{
            backgroundColor: selectedLevel === 0 ? 'var(--bg-tertiary)' : `${(BOOK_LEVELS.find(l=>l.id===selectedLevel)||{}).color}12`,
            color: selectedLevel === 0 ? 'var(--text-primary)' : (BOOK_LEVELS.find(l=>l.id===selectedLevel)||{}).color,
            border: `2px solid ${selectedLevel === 0 ? 'var(--border-color)' : (BOOK_LEVELS.find(l=>l.id===selectedLevel)||{}).color}`,
            outline: 'none', minWidth: '220px',
          }}
          data-testid="level-select">
          <option value={0}>{sv ? 'Alla nivåer' : 'All levels'}</option>
          {BOOK_LEVELS.map(l => <option key={l.id} value={l.id}>{sv ? l.name_sv : l.name_en}</option>)}
        </select>
        <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" style={{ color: 'var(--text-muted)' }}/>
      </div>
    </div>
    {BOOK_LEVELS.filter(level => selectedLevel === 0 || level.id === selectedLevel).map(level => {
      const levelChapters = level.chapterIds.map(id => chapters.find(ch => ch.id === id)).filter(Boolean);
      if (levelChapters.length === 0) return null;
      return (
        <div key={level.id} className="mb-2">
          <div className="flex items-center gap-3 mb-3 mt-6 first:mt-0" data-testid={`level-header-${level.id}`}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${level.color}18`, border: `2px solid ${level.color}` }}>
              <span style={{ fontFamily: "'Scheherazade New', serif", fontSize: '1.2rem', color: level.color, fontWeight: 700 }}>{level.icon}</span>
            </div>
            <div className="flex-1">
              <p className="font-bold text-base" style={{ color: level.color }}>{sv ? level.name_sv : level.name_en}</p>
              <p className="text-xs" style={{ fontFamily: "'Scheherazade New', serif", color: 'var(--text-muted)' }}>{level.arabicTitle} — {levelChapters.length} {sv ? 'kapitel' : 'chapters'}</p>
            </div>
            <div className="h-px flex-1" style={{ backgroundColor: level.color, opacity: 0.25 }}/>
          </div>
          <div className="space-y-2">
            {levelChapters.map(ch => (
              <button key={ch.id} onClick={() => onSelectChapter(ch.id)}
                className="w-full p-4 rounded-lg text-left flex items-center justify-between group transition-all hover:scale-[1.01]"
                style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderLeft: `4px solid ${level.color}` }}
                data-testid={`tajweed-chapter-${ch.id}`}>
                <div className="flex items-center gap-4">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: `${level.color}18`, color: level.color }}>{ch.id}</span>
                  <div>
                    <p className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{sv ? ch.title : (ch.title_en || ch.title)}</p>
                    <p className="text-xs" style={{ fontFamily: "'Scheherazade New', serif", color: 'var(--text-muted)' }}>{ch.arabicTitle}</p>
                  </div>
                </div>
                <ChevronRight size={18} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: level.color }}/>
              </button>
            ))}
          </div>
        </div>
      );
    })}
  </div>
));

// ==========================================
// LESSON CONTENT WRAPPER
// ==========================================
const LessonContent = ({ chapterId, bookLang, isAdmin, sv, onNavigate, onBackToToc }) => {
  const chapterIdx = chapters.findIndex(c => c.id === chapterId);
  const chapter = chapters[chapterIdx];
  const contentRef = useRef(null);
  const LessonComponent = lessonLoaders[chapterId];
  const prevChapter = chapterIdx > 0 ? chapters[chapterIdx - 1] : null;
  const nextChapter = chapterIdx < chapters.length - 1 ? chapters[chapterIdx + 1] : null;
  const level = getChapterLevel(chapterId);

  return (
    <div ref={contentRef} className="tajweed-book-content" data-testid="tajweed-chapter-content">
      <div className="mb-6 pb-4" style={{ borderBottom: '2px solid var(--gold)' }}>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm text-gold font-medium">{sv ? 'Kapitel' : 'Chapter'} {chapter.id} {sv ? 'av' : 'of'} {chapters.length}</span>
          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${level.color}15`, color: level.color, border: `1px solid ${level.color}40` }}>
            {sv ? level.name_sv : level.name_en}
          </span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{sv ? chapter.title : (chapter.title_en || chapter.title)}</h1>
        <p style={{ fontFamily: "'Scheherazade New', serif", fontSize: '1.3rem', color: 'var(--text-muted)' }}>{chapter.arabicTitle}</p>
      </div>

      <Suspense fallback={<LessonLoader />}>
        {LessonComponent && <LessonComponent lang={bookLang} admin={isAdmin} />}
      </Suspense>

      <div className="flex justify-between mt-12 pt-6" style={{ borderTop: '1px solid var(--border-color)' }}>
        <button onClick={() => prevChapter ? onNavigate(prevChapter.id) : onBackToToc()}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors hover:bg-gold/10" style={{ color: 'var(--text-secondary)' }}>
          <ChevronLeft size={18}/> {prevChapter ? (sv ? prevChapter.title : (prevChapter.title_en || prevChapter.title)) : (sv ? 'Innehåll' : 'Contents')}
        </button>
        {nextChapter ? (
          <button onClick={() => onNavigate(nextChapter.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg btn-gold">
            {sv ? nextChapter.title : (nextChapter.title_en || nextChapter.title)} <ChevronRight size={18}/>
          </button>
        ) : (
          <button onClick={onBackToToc} className="flex items-center gap-2 px-4 py-2 rounded-lg btn-gold">
            {sv ? 'Tillbaka till innehåll' : 'Back to contents'} <BookOpen size={18}/>
          </button>
        )}
      </div>
    </div>
  );
};

// ==========================================
// MAIN BOOK COMPONENT
// ==========================================
const TajweedBookPage = () => {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authed, setAuthed] = useState(sessionStorage.getItem('tajweed-auth') === 'true');
  const [userRole, setUserRole] = useState(sessionStorage.getItem('tajweed-role') || 'admin');
  const [userName, setUserName] = useState(sessionStorage.getItem('tajweed-name') || '');
  const [bookLang, setBookLang] = useState('sv');
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const lessonParam = searchParams.get('lesson');
  const activeLessonId = lessonParam ? parseInt(lessonParam) : null;
  const showToc = !activeLessonId && !showQuiz;
  const sv = bookLang === 'sv';
  const isAdmin = userRole === 'admin';
  const activeChapter = activeLessonId ? chapters.find(c => c.id === activeLessonId) : null;

  useEffect(() => {
    if (authed) return;
    const params = new URLSearchParams(window.location.search);
    const nyckel = params.get('nyckel');
    if (nyckel && nyckel.trim() === STUDENT_KEY) {
      sessionStorage.setItem('tajweed-auth', 'true');
      sessionStorage.setItem('tajweed-role', 'student');
      sessionStorage.setItem('tajweed-name', 'Elev');
      setAuthed(true); setUserRole('student'); setUserName('Elev');
    }
  }, [authed]);

  const navigateToLesson = useCallback((id) => {
    setSearchParams({ lesson: id });
    setShowQuiz(false);
    window.scrollTo(0, 0);
  }, [setSearchParams]);

  const navigateToToc = useCallback(() => {
    setSearchParams({});
    setShowQuiz(false);
  }, [setSearchParams]);

  const handleLogin = (userData) => {
    setAuthed(true);
    setUserRole(userData.role);
    setUserName(userData.name || '');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('tajweed-auth');
    sessionStorage.removeItem('tajweed-role');
    sessionStorage.removeItem('tajweed-name');
    sessionStorage.removeItem('tajweed-token');
    setAuthed(false);
    setUserRole('admin');
    setUserName('');
  };

  // Anti-piracy protection for students
  useEffect(() => {
    if (!authed || isAdmin) return;
    const preventCopy = (e) => e.preventDefault();
    const preventKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['p','P','s','S','c','C','a','A','u','U'].includes(e.key)) e.preventDefault();
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ['I','i','J','j','C','c'].includes(e.key)) e.preventDefault();
      if (e.key === 'F12') e.preventDefault();
      if (e.key === 'PrintScreen') { e.preventDefault(); document.body.style.filter = 'blur(30px)'; setTimeout(() => { document.body.style.filter = 'none'; }, 1500); }
    };
    const preventContextMenu = (e) => e.preventDefault();
    const preventDrag = (e) => e.preventDefault();
    const handleVisibility = () => {
      if (document.hidden) document.body.style.filter = 'blur(20px)';
      else setTimeout(() => { document.body.style.filter = 'none'; }, 300);
    };
    const printStyle = document.createElement('style');
    printStyle.id = 'tajweed-print-block';
    printStyle.textContent = `@media print { body * { display: none !important; visibility: hidden !important; } body::after { content: "Upphovsrattsskyddad - Kopiering ej tillatenn / Copyright protected"; display: block !important; visibility: visible !important; font-size: 24px; text-align: center; padding: 100px 20px; color: #CE9608; } }`;
    document.head.appendChild(printStyle);
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);
    document.addEventListener('keydown', preventKeys);
    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('drag', preventDrag);
    document.addEventListener('dragstart', preventDrag);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
      document.removeEventListener('keydown', preventKeys);
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('drag', preventDrag);
      document.removeEventListener('dragstart', preventDrag);
      document.removeEventListener('visibilitychange', handleVisibility);
      document.body.style.filter = 'none';
      const ps = document.getElementById('tajweed-print-block');
      if (ps) ps.remove();
    };
  }, [authed, isAdmin]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const container = document.createElement('div');
      container.style.cssText = 'padding:30px;background:#fff;color:#000;max-width:800px;';
      container.innerHTML = `<div style="text-align:center;margin-bottom:40px;padding:30px;border-bottom:3px solid #CE9608;"><h1 style="font-size:28px;color:#0B0B0B;">Tajweed Guide</h1><p style="font-size:20px;font-family:'Scheherazade New',serif;color:#CE9608;">التجويد المصوّر</p><p style="color:#666;font-size:14px;">Av Ustadh Ismail</p></div>`;
      const opt = { margin: [10, 15], filename: 'Tajweed-Illustrerad-Guide-Ustadh-Ismail.pdf', image: { type: 'jpeg', quality: 0.95 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } };
      await html2pdf().set(opt).from(container).save();
    } catch (err) { window.print(); }
    setDownloading(false);
  };

  if (!authed) return <BookLogin onLogin={handleLogin} />;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)', userSelect: isAdmin ? 'auto' : 'none', WebkitUserSelect: isAdmin ? 'auto' : 'none', WebkitTouchCallout: isAdmin ? 'default' : 'none', MozUserSelect: isAdmin ? 'auto' : 'none', msUserSelect: isAdmin ? 'auto' : 'none' }}>
      {!isAdmin && <style>{`.tajweed-book-content img { pointer-events: none; -webkit-user-drag: none; user-drag: none; } .tajweed-book-content { -webkit-print-color-adjust: exact; }`}</style>}
      {!isAdmin && userName && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9999, display: 'flex', flexWrap: 'wrap', alignContent: 'center', justifyContent: 'center', gap: '100px', opacity: 0.04, transform: 'rotate(-30deg)', overflow: 'hidden' }}>
          {Array.from({ length: 12 }).map((_, i) => <span key={i} style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{userName}</span>)}
        </div>
      )}
      {/* Header */}
      <div className="pt-20 pb-10 text-center relative" style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '2px solid var(--gold)' }}>
        <p className="text-sm tracking-widest mb-2" style={{ color: 'var(--gold)' }}>QURAN ONLINE SVERIGE</p>
        <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Tajweed Guide</h1>
        <p style={{ fontFamily: "'Scheherazade New', serif", fontSize: '1.5rem', color: 'var(--gold)' }}>كتاب التجويد</p>
        <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>{sv ? 'Av Ustadh Ismail' : 'By Ustadh Ismail'}</p>
        <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
          <button onClick={() => setBookLang(bookLang === 'sv' ? 'en' : 'sv')}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
            style={{ backgroundColor: 'rgba(206,150,8,0.1)', color: '#CE9608', border: '1px solid rgba(206,150,8,0.3)' }}
            data-testid="book-lang-toggle">
            <Globe size={14}/> {bookLang === 'sv' ? 'EN' : 'SV'}
          </button>
          <button onClick={() => { setShowQuiz(true); setSearchParams({}); window.scrollTo(0,0); }}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
            style={{ backgroundColor: 'rgba(168,85,247,0.1)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.3)' }}
            data-testid="quiz-mode-btn">
            <Sparkles size={14}/> Quiz
          </button>
          {!isAdmin && <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}><Shield size={12} className="inline mr-1"/>{userName}</span>}
          <button onClick={handleLogout}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
            style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}
            data-testid="book-logout-btn">
            <LogOut size={14}/> {sv ? 'Logga ut' : 'Logout'}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Link to="/" className="hover:text-gold transition-colors"><Home size={14}/></Link>
          <ChevronRight size={14}/>
          <span className="cursor-pointer hover:text-gold transition-colors" onClick={navigateToToc}>{sv ? 'Tajweed Guide' : 'Tajweed Guide'}</span>
          {showQuiz && <><ChevronRight size={14}/><span style={{ color: '#a855f7' }}>{sv ? 'Quiz-läge' : 'Quiz Mode'}</span></>}
          {activeChapter && <><ChevronRight size={14}/><span style={{ color: 'var(--text-primary)' }}>{sv ? activeChapter.title : (activeChapter.title_en || activeChapter.title)}</span></>}
        </div>

        {showQuiz ? (
          <TajweedQuiz lang={bookLang} onClose={() => { setShowQuiz(false); navigateToToc(); }}/>
        ) : showToc ? (
          <BookTOC sv={sv} selectedLevel={selectedLevel} setSelectedLevel={setSelectedLevel} onSelectChapter={navigateToLesson} />
        ) : activeLessonId ? (
          <LessonContent chapterId={activeLessonId} bookLang={bookLang} isAdmin={isAdmin} sv={sv} onNavigate={navigateToLesson} onBackToToc={navigateToToc} />
        ) : null}
      </div>
    </div>
  );
};

export default TajweedBookPage;
